/**
 * Auto-fit text inside a DOM element.
 *
 * Iteratively shrinks `font-size` (binary search) until the element's content
 * fits within the box at the given min/max bounds. Uses a `ResizeObserver` so
 * the text re-fits whenever the container changes size (e.g. on window resize).
 *
 * Usage:
 *   const el = document.querySelector('.tile-text');
 *   fitText(el, { min: 10, max: 64 });
 *
 * Re-call `fitText(el, ...)` after the text content changes; the helper takes
 * a fresh measurement on every call. The ResizeObserver only re-fits on
 * geometry changes — for content changes call `fitText` again explicitly.
 */

const MIN_DEFAULT = 10;
const MAX_DEFAULT = 64;

export interface FitTextOptions {
  /** Smallest font-size to try, in px. Default 10. */
  min?: number;
  /** Largest font-size to try, in px. Default 64. */
  max?: number;
  /**
   * Optional line-height multiplier. Some browsers compute line-height as a
   * fixed px value once font-size shrinks; multiplier keeps it proportional.
   * Default 1.1.
   */
  lineHeight?: number;
  /**
   * If true, observe the container with a ResizeObserver and re-fit on size
   * change. Default true.
   */
  observe?: boolean;
}

interface AttachedState {
  el: HTMLElement;
  opts: Required<FitTextOptions>;
  observer: ResizeObserver | null;
  rafToken: number | null;
}

const ATTACHED = new WeakMap<HTMLElement, AttachedState>();

/**
 * Fit text inside `el`. Idempotent — call any time content or container
 * dimensions change. Returns the chosen font-size in px (so callers can log
 * for diagnostics).
 */
export function fitText(el: HTMLElement, opts: FitTextOptions = {}): number {
  const min = opts.min ?? MIN_DEFAULT;
  const max = opts.max ?? MAX_DEFAULT;
  const lineHeight = opts.lineHeight ?? 1.1;
  const observe = opts.observe ?? true;

  const resolved: Required<FitTextOptions> = { min, max, lineHeight, observe };

  // Container must have a size; if not yet laid out, defer one frame.
  const w = el.clientWidth;
  const h = el.clientHeight;
  if (w === 0 || h === 0) {
    // Defer to next frame; ResizeObserver will catch up once the parent lays out.
    requestAnimationFrame(() => fitText(el, opts));
    if (observe) attach(el, resolved);
    return max;
  }

  // Apply line-height once.
  el.style.lineHeight = String(lineHeight);
  // Make sure overflow is hidden so we can detect overflow correctly during measure.
  if (!el.style.overflow) el.style.overflow = 'hidden';

  const fits = (size: number): boolean => {
    el.style.fontSize = `${size}px`;
    // Account for both width and height overflow.
    return el.scrollWidth <= el.clientWidth + 1 && el.scrollHeight <= el.clientHeight + 1;
  };

  // Binary search the largest size that fits.
  let lo = min;
  let hi = max;
  // Quick path: max already fits.
  if (fits(hi)) {
    el.style.fontSize = `${hi}px`;
    if (observe) attach(el, resolved);
    return hi;
  }
  // Quick path: min doesn't fit. Force min and let CSS truncate.
  if (!fits(lo)) {
    el.style.fontSize = `${lo}px`;
    if (observe) attach(el, resolved);
    return lo;
  }
  // Standard binary search to ~0.5px precision.
  while (hi - lo > 0.5) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  el.style.fontSize = `${Math.floor(lo)}px`;
  if (observe) attach(el, resolved);
  return Math.floor(lo);
}

function attach(el: HTMLElement, opts: Required<FitTextOptions>): void {
  const existing = ATTACHED.get(el);
  if (existing) {
    existing.opts = opts;
    return;
  }
  if (typeof ResizeObserver === 'undefined') {
    ATTACHED.set(el, { el, opts, observer: null, rafToken: null });
    return;
  }
  const state: AttachedState = { el, opts, observer: null, rafToken: null };
  const observer = new ResizeObserver(() => {
    // Coalesce rapid resize events into one rAF.
    if (state.rafToken !== null) return;
    state.rafToken = requestAnimationFrame(() => {
      state.rafToken = null;
      fitText(state.el, { ...state.opts, observe: false });
    });
  });
  observer.observe(el);
  state.observer = observer;
  ATTACHED.set(el, state);
}

/** Stop observing an element (use on dispose). */
export function unfitText(el: HTMLElement): void {
  const state = ATTACHED.get(el);
  if (!state) return;
  if (state.rafToken !== null) cancelAnimationFrame(state.rafToken);
  if (state.observer) state.observer.disconnect();
  ATTACHED.delete(el);
}
